// NPM packages
import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTheme, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

// All other imports
import { useServices } from 'services';
import useResourceGuard from 'hooks/useResourceGuard';
import PageContent from 'components/core/PageContent';
import AdditionalResource from './AdditionalResource';
import FeaturedResource from './FeaturedResource';
import DataProducts from './DataProducts';
import Statistics from './Statistics';
import JoinButton from 'components/project/JoinButton';
import Project from 'types/Project';
import Link from 'components/core/Link';

const PageTitle = styled(Typography)(({ theme }) => ({
    flex: '1 1 auto',
    minWidth: 0,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    [theme.breakpoints.down('xl')]: {
        fontSize: theme.typography.pxToRem(28),
    },
    [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(24),
    },
})) as typeof Typography;

const SectionHeader = styled(Typography)(({ theme }) => ({
    marginTop: '0',
    marginBottom: '0',
    [theme.breakpoints.down('xl')]: {
        fontSize: theme.typography.pxToRem(22),
    },
    [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(20),
    },
})) as typeof Typography;

const BodyText = styled(Typography)(({ theme }) => ({
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    [theme.breakpoints.down('xl')]: {
        fontSize: theme.typography.pxToRem(16),
    },
    [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.pxToRem(14),
    },
})) as typeof Typography;

const bsecSettings = {
    msdliveProjectSearchKey: 'BSEC UIFL',
    msdliveProjectStatsKey: 'msdlive-project-bsec',
    msdliveProjectSlug: 'bsec',
    embeddedDashboard: {
        title: 'BSEC Weather Station Dashboard',
        description: 'Location of weather stations (instruments that measure temperature, humidity, rainfall, wind, and sunlight) operated by BSEC. Color shows type of instrument: Ambient Weather (green) and OttHydro (purple).',
        dataUrl: 'https://data.msdlive.org/records/6yawb-zyx60/latest',
        url: 'https://msd-live.github.io/bsec-dashboard/',
    },
    informationalLinks: [
        {
            name: 'BSEC Homepage',
            description: 'Follow for news and updates from the BSEC team!',
            url: 'https://bsec.21cc.jhu.edu/',
        },
        {
            name: 'CoURAGE',
            description: 'Coast-Urban-Rural Atmospheric Gradient Experiment',
            url: 'https://www.arm.gov/research/campaigns/amf2024courage',
        },
    ],
    featuredResources: [
        {
            url: 'https://data.msdlive.org/records/6yawb-zyx60/latest',
            title: 'BSEC Weather Station Data',
            summary: 'Measurements of temperature, humidity, rainfall, wind, and sunlight across Baltimore city.',
            img: '/bsec/weather_station.jpg',
        },
        {
            url: 'https://data.msdlive.org/records/x1x98-dxr27/latest',
            title: 'BSEC Air Quality Data',
            summary: 'Measurements of the concentrations and attenuations of various aerosols in ten minute averages.',
            img: '/bsec/air_quality.jpg',
        },
    ]
};

const Root = styled(PageContent)(({ theme }) => ({
    paddingTop: '1rem!important',
    [theme.breakpoints.down('xl')]: {
        padding: `0 ${theme.typography.pxToRem(43.5)}`,
        paddingTop: '1rem!important',
    },
    [theme.breakpoints.down('md')]: {
        padding: '0 1rem',
        paddingTop: '1rem!important',
    },
}));

const Flexy = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    minWidth: 0,
    flex: '1 1 24rem',
    alignItems: 'flex-start',
    [theme.breakpoints.up('lg')]: {
        alignItems: 'flex-end',
    },
}));

export interface BSECLandingProps {
    className?: string;
  }

function BSECLanding(props: BSECLandingProps): React.ReactElement | null {

    const { className } = props;

    const theme = useTheme();


    const { projectService } = useServices();

    const location = useLocation();

    useEffect(() => {
        if (projectService && location) {
            const slug = location?.pathname?.split('/')?.[2];
            if (slug) {
                projectService.selectProject(slug);
            }
        }
    }, [projectService, location])

    const guardContent = useResourceGuard({
        resource: projectService.selectedProject,
    });

    let body: React.ReactNode;

    if (guardContent) {
        body = guardContent;
    } else {

        const project = projectService.selectedProject as Project;

        body = (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '2rem',
                        marginBottom: '1rem',
                    }}
                >
                    <img
                        alt="BSEC"
                        style={{
                            height: '9rem',
                            maxWidth: '12rem',
                            objectFit: 'contain',
                        }}
                        src={project.links.logo}
                    />
                    <PageTitle variant='h2'>
                        Baltimore Social-Environmental Collaborative
                    </PageTitle>
                    <div style={{ flexShrink: 0 }}>
                        <JoinButton project={project} size="medium" includeEdit includeMembers />
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        rowGap: '2rem',
                        columnGap: '1rem',
                        marginTop: '-1.5rem',
                    }}
                >
                    <div
                        style={{
                            flex: '1 1 24rem',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                            minWidth: 0,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                    gap: '1rem',
                                    marginTop: '0.5rem',
                                }}
                            >
                                <SectionHeader variant='h5'>
                                    About
                                </SectionHeader>
                            </div>
                            <BodyText variant='body1'>
                                {project.description}
                                <br/><br/>
                                MSD-LIVE Contact: <Link
                                    to={`mailto:${project.contact.email}`}
                                >{project.contact.firstName} {project.contact.lastName}</Link>
                            </BodyText>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                    gap: '1rem',
                                    marginTop: '1rem',
                                }}
                            >
                                <Typography variant='h6'>
                                    Additional Resources
                                </Typography>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.25rem',
                                }}
                            >
                                {bsecSettings.informationalLinks.map((l, i) => (
                                    <AdditionalResource
                                        key={i}
                                        {...l}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <Flexy>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-end',
                                    gap: '1rem',
                                    marginTop: '0.5rem',
                                }}
                            >
                                <SectionHeader variant='h5'>
                                    Highlights
                                </SectionHeader>
                            </div>
                            <div
                                style={{
                                    paddingInline: '1.5rem',
                                    paddingTop: '1rem',
                                    paddingBottom: '1.25rem',
                                    borderRadius: '0.5rem',
                                    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                                    backgroundColor: '#f3f4f6',
                                }}
                            >
                                <Statistics
                                    project={bsecSettings.msdliveProjectStatsKey}
                                    projectUploadsKey={bsecSettings.msdliveProjectSlug}
                                    projectDatasetKey={project.slug}
                                    projectSearchKey={bsecSettings.msdliveProjectSearchKey}
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-end',
                                        gap: '1rem',
                                        marginTop: '1.25rem',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        Featured Datasets
                                    </Typography>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem',
                                    }}
                                >
                                    {bsecSettings.featuredResources.map((r, i) => (
                                        <FeaturedResource
                                            key={i}
                                            {...r}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Flexy>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        flex: '1',
                        flexShrink: '0',
                        width: '100%',
                        minWidth: 0,
                        marginTop: '1rem',
                    }}
                >
                    {bsecSettings.embeddedDashboard && (
                        <>
                            <Typography variant="h6">
                                {bsecSettings.embeddedDashboard.title}
                            </Typography>
                            <BodyText style={{maxWidth: 'unset'}}>
                                {bsecSettings.embeddedDashboard.description} Get the data by clicking <Link to={bsecSettings.embeddedDashboard.dataUrl} openNewTab>here</Link>, or open in full screen by clicking <Link to={bsecSettings.embeddedDashboard.url} openNewTab>here</Link>.
                            </BodyText>
                            <div
                                style={{
                                    width: '100%',
                                    height: '50rem',
                                    marginBottom: '2rem',
                                }}
                            >
                                <iframe
                                    title={bsecSettings.embeddedDashboard.title}
                                    src={bsecSettings.embeddedDashboard.url}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                                    }}
                                />
                            </div>
                        </>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-end',
                            gap: '1rem',
                            marginTop: '0.5rem',
                        }}
                    >
                        <Typography variant='h6'>
                            Data Products
                        </Typography>
                    </div>
                    <DataProducts projectSearchKey={bsecSettings.msdliveProjectSearchKey} />
                </div>
            </div>
        );
    }

    return (
        <Root className={className} maxWidth={theme.maxToolWidth}>
            {body}
        </Root>
      );

}

export default observer(BSECLanding);
